import { useMemo } from 'react';
import { useAuth } from '@/features/auth';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import { isRoleAllowed } from '@/shared/constants/auth/roles.constant.ts';
import { BOARD_PERMISSIONS } from '../constants/permissions.ts';
import type { Post } from '../types/board.type.ts';

interface BoardPermissions {
  /** 로그인 여부 */
  isAuthenticated: boolean;
  /** 글 작성 가능 */
  canCreate: boolean;
  /** 특정 글 수정 가능 여부 판단 함수 (소유권 + Role 합산) */
  canUpdate: (post?: Pick<Post, 'authorId'>) => boolean;
  /** 특정 글 삭제 가능 여부 판단 함수 */
  canDelete: (post?: Pick<Post, 'authorId'>) => boolean;
}

/**
 * 게시판 권한 판단 훅.
 *
 * 권한 = (Role 정책) OR (작성자 본인 + 자가관리 허용).
 *
 * ⚠️ FE 권한 체크는 어디까지나 UX(버튼 노출/라우트 가드)용이다.
 *    실제 인가는 Gateway/서비스에서 x-user-role/x-user-id 로 최종 강제한다.
 *    FE 를 우회해도 서버가 막아야 한다 — FE 검사만 믿지 말 것.
 *
 * user.userRole(auth.types UserRole)과 roles.constants UserRoleValue 가 이제
 * 동일 타입이라 별도 캐스팅 없이 그대로 사용한다.
 */
export function useBoardPermissions(type: BoardType): BoardPermissions {
  const { user, isAuthenticated } = useAuth();
  const policy = BOARD_PERMISSIONS[type];

  return useMemo(() => {
    const role = user?.userRole ?? null;
    const userId = user?.id ?? null;

    const canCreate = isRoleAllowed(role, policy.create);

    const ownsPost = (post?: Pick<Post, 'authorId'>) =>
      !!userId && !!post?.authorId && post.authorId === userId;

    const canUpdate = (post?: Pick<Post, 'authorId'>) => {
      if (isRoleAllowed(role, policy.update)) return true;
      return policy.allowAuthorSelfManage && ownsPost(post);
    };

    const canDelete = (post?: Pick<Post, 'authorId'>) => {
      if (isRoleAllowed(role, policy.delete)) return true;
      return policy.allowAuthorSelfManage && ownsPost(post);
    };

    return { isAuthenticated, canCreate, canUpdate, canDelete };
  }, [user, isAuthenticated, policy]);
}
